import React, { useState, useEffect } from "react";
import { Table, Button, Tooltip, Tag } from "antd";
import {
  PlusOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { SupplierDetails } from "./SupplierDetails";
import type { Category, Location } from "../hooks/useFinanceData";
import type {
  SupplierCustomValue,
  DailyRevenueSupplierExpense,
  SupplierDefaultValue,
} from "../../../utils/api/supplierExpensesAPI";

interface SupplierTableData {
  key: string;
  supplierId: string;
  supplierName: string;
  categoryId: string;
  locationTotals: Record<string, { rsd: number; eur: number }>;
  totalRsd: number;
  totalEur: number;
}

interface SupplierTableProps {
  category: Category;
  suppliers: Array<{ _id: string; name: string }>;
  locations: Location[];
  defaultValues: SupplierDefaultValue[];
  customValues: SupplierCustomValue[];
  dailyRevenueExpenses: DailyRevenueSupplierExpense[];
  canUpdateMatrix: boolean;
  onAddCustomValue: (supplierData: {
    supplierId: string;
    supplierName: string;
    categoryId: string;
    locationId: string;
  }) => void;
  onEditCustomValue: (
    customValue: any,
    supplierData: {
      supplierId: string;
      supplierName: string;
      categoryId: string;
      locationId: string;
    },
  ) => void;
  onDeleteCustomValue: (id: string) => void;
  onRefresh: () => Promise<void>;
  highlightCustomValueId?: string;
  autoExpandSupplierId?: string;
}

export const SupplierTable: React.FC<SupplierTableProps> = ({
  category,
  suppliers,
  locations,
  defaultValues,
  customValues,
  dailyRevenueExpenses,
  canUpdateMatrix,
  onAddCustomValue,
  onEditCustomValue,
  onDeleteCustomValue,
  onRefresh,
  highlightCustomValueId,
  autoExpandSupplierId,
}) => {
  const { t } = useTranslation();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    if (autoExpandSupplierId) {
      setExpandedRowKeys((prev) =>
        prev.includes(autoExpandSupplierId) ? prev : [...prev, autoExpandSupplierId],
      );
    }
  }, [autoExpandSupplierId]);

  // Calculate totals per supplier per location
  const calculateTotals = (
    supplierId: string,
    locationId: string,
  ): { rsd: number; eur: number } => {
    let rsd = 0;
    let eur = 0;

    // Default values (fixed monthly costs) - sum ALL matches for multi-month periods
    const matchingDefaultValues = defaultValues.filter(
      (dv) =>
        dv.supplierId === supplierId &&
        dv.categoryId === category._id &&
        dv.locationId === locationId,
    );
    matchingDefaultValues.forEach((dv) => {
      rsd += dv.defaultValue || 0;
    });

    // Custom values - exclude generated fixed expenses (type === "fixed") as they
    // are already accounted for via defaultValues
    const locationCustomValues = customValues.filter(
      (cv) =>
        cv.type !== "fixed" &&
        cv.supplierId === supplierId &&
        (cv.locationId === locationId ||
          (cv.locationId as any)?._id === locationId),
    );
    locationCustomValues.forEach((cv) => {
      if (cv.currency === "RSD") {
        rsd += cv.amount || 0;
      } else if (cv.currency === "EUR") {
        eur += cv.amount || 0;
      }
    });

    // Daily revenue expenses (always RSD, no currency field)
    const locationDailyExpenses = dailyRevenueExpenses.filter(
      (exp) =>
        exp.supplierId === supplierId &&
        (exp.locationId === locationId ||
          (exp.locationId as any)?._id === locationId),
    );
    locationDailyExpenses.forEach((exp) => {
      rsd += exp.amount || 0;
    });

    return { rsd, eur };
  };

  // Return generated fixed expenses for a specific supplier + location
  const getFixedExpenses = (supplierId: string, locationId: string) =>
    customValues.filter(
      (cv) =>
        cv.type === "fixed" &&
        cv.supplierId === supplierId &&
        (cv.locationId === locationId ||
          (cv.locationId as any)?._id === locationId),
    );

  // Compact status indicator for fixed expenses in a location cell
  const renderFixedStatus = (supplierId: string, locationId: string) => {
    const fixed = getFixedExpenses(supplierId, locationId);
    if (fixed.length === 0) return null;

    const paid = fixed.filter((f) => f.paymentStatus === "paid").length;
    const unpaid = fixed.filter((f) => f.paymentStatus === "unpaid").length;
    const partial = fixed.filter((f) => f.paymentStatus === "partial").length;

    const parts: React.ReactNode[] = [];

    if (paid > 0) {
      parts.push(
        <Tooltip
          key="paid"
          title={`${paid} fixed ${paid === 1 ? "expense" : "expenses"} paid`}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 11 }} />
            {fixed.length > 1 && (
              <span style={{ fontSize: 10, color: "#52c41a" }}>{paid}</span>
            )}
          </span>
        </Tooltip>,
      );
    }
    if (partial > 0) {
      parts.push(
        <Tooltip
          key="partial"
          title={`${partial} fixed ${partial === 1 ? "expense" : "expenses"} partially paid`}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <ClockCircleFilled style={{ color: "#faad14", fontSize: 11 }} />
            {fixed.length > 1 && (
              <span style={{ fontSize: 10, color: "#faad14" }}>{partial}</span>
            )}
          </span>
        </Tooltip>,
      );
    }
    if (unpaid > 0) {
      parts.push(
        <Tooltip
          key="unpaid"
          title={`${unpaid} fixed ${unpaid === 1 ? "expense" : "expenses"} unpaid`}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 11 }} />
            {fixed.length > 1 && (
              <span style={{ fontSize: 10, color: "#ff4d4f" }}>{unpaid}</span>
            )}
          </span>
        </Tooltip>,
      );
    }

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          marginTop: 3,
        }}
      >
        <Tag
          style={{
            fontSize: 9,
            padding: "0 4px",
            lineHeight: "16px",
            marginRight: 2,
            border: "none",
            background: "#f0f0f0",
            color: "#595959",
          }}
        >
          Fixed
        </Tag>
        {parts}
      </div>
    );
  };

  // Build table data
  const tableData: SupplierTableData[] = suppliers
    .map((supplier) => {
      const locationTotals: Record<string, { rsd: number; eur: number }> = {};
      let totalRsd = 0;
      let totalEur = 0;

      locations.forEach((location) => {
        const totals = calculateTotals(supplier._id, location._id);
        locationTotals[location._id] = totals;
        totalRsd += totals.rsd;
        totalEur += totals.eur;
      });

      return {
        key: supplier._id,
        supplierId: supplier._id,
        supplierName: supplier.name,
        categoryId: category._id,
        locationTotals,
        totalRsd,
        totalEur,
      };
    })
    .sort((a, b) => b.totalRsd - a.totalRsd || b.totalEur - a.totalEur);

  // Format currency with improved UX
  const formatCurrency = (rsd: number, eur: number): React.ReactNode => {
    const hasRsd = rsd > 0;
    const hasEur = eur > 0;

    if (!hasRsd && !hasEur) {
      return <span style={{ color: "#bfbfbf" }}>-</span>;
    }

    // If only one currency, show simple format
    if (hasRsd && !hasEur) {
      return (
        <div style={{ textAlign: "right" }}>
          <span style={{ fontWeight: 500 }}>{rsd.toLocaleString()}</span>
          <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>
            RSD
          </span>
        </div>
      );
    }

    if (hasEur && !hasRsd) {
      return (
        <div style={{ textAlign: "right" }}>
          <span style={{ fontWeight: 500, color: "#1890ff" }}>
            {eur.toLocaleString()}
          </span>
          <span style={{ fontSize: 11, color: "#1890ff", marginLeft: 4 }}>
            EUR
          </span>
        </div>
      );
    }

    // Both currencies - vertical stack for better readability
    return (
      <div style={{ textAlign: "right", lineHeight: 1.4 }}>
        <div style={{ marginBottom: 2 }}>
          <span style={{ fontWeight: 500 }}>{rsd.toLocaleString()}</span>
          <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>
            RSD
          </span>
        </div>
        <div>
          <span style={{ fontWeight: 500, color: "#1890ff" }}>
            {eur.toLocaleString()}
          </span>
          <span style={{ fontSize: 11, color: "#1890ff", marginLeft: 4 }}>
            EUR
          </span>
        </div>
      </div>
    );
  };

  // Build columns
  const columns = [
    {
      title: t("Supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      fixed: "left" as const,
      width: 160,
      ellipsis: true,
      render: (name: string) => (
        <strong style={{ fontSize: 13 }}>{name}</strong>
      ),
    },
    ...locations.map((location) => ({
      title: location.name,
      key: location._id,
      width: 130,
      ellipsis: true,
      render: (_: any, record: SupplierTableData) => {
        const totals = record.locationTotals[location._id];
        const fixedStatus = renderFixedStatus(
          record.supplierId,
          location._id,
        );
        return (
          <div className="location-cell">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{formatCurrency(totals.rsd, totals.eur)}</span>
              {canUpdateMatrix && (
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  className="add-expense-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddCustomValue({
                      supplierId: record.supplierId,
                      supplierName: record.supplierName,
                      categoryId: record.categoryId,
                      locationId: location._id,
                    });
                  }}
                  style={{ opacity: 0, transition: "opacity 0.2s" }}
                />
              )}
            </div>
            {fixedStatus}
          </div>
        );
      },
    })),
    {
      title: t("Total"),
      key: "total",
      fixed: "right" as const,
      width: 130,
      render: (_: any, record: SupplierTableData) => (
        <strong style={{ fontSize: 13 }}>
          {formatCurrency(record.totalRsd, record.totalEur)}
        </strong>
      ),
    },
  ];

  return (
    <div className="supplier-table-wrapper">
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 900 }}
        size="small"
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(
              expanded
                ? [...expandedRowKeys, record.key]
                : expandedRowKeys.filter((key) => key !== record.key),
            );
          },
          expandedRowRender: (record) => (
            <div className="supplier-details-wrapper">
              <SupplierDetails
                supplierId={record.supplierId}
                supplierName={record.supplierName}
                categoryId={record.categoryId}
                locations={locations}
                defaultValues={defaultValues}
                customValues={customValues}
                dailyRevenueExpenses={dailyRevenueExpenses}
                canUpdateMatrix={canUpdateMatrix}
                onAddCustomValue={onAddCustomValue}
                onEditCustomValue={onEditCustomValue}
                onDeleteCustomValue={onDeleteCustomValue}
                onRefresh={onRefresh}
                highlightCustomValueId={
                  record.supplierId === autoExpandSupplierId
                    ? highlightCustomValueId
                    : undefined
                }
              />
            </div>
          ),
        }}
        rowClassName="supplier-row"
        onRow={(record) => ({
          onClick: () => {
            setExpandedRowKeys(
              expandedRowKeys.includes(record.key)
                ? expandedRowKeys.filter((key) => key !== record.key)
                : [...expandedRowKeys, record.key],
            );
          },
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default SupplierTable;
